import React, { useEffect, useId } from 'react';
import { createPortal } from 'react-dom';
import { X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { IconButton } from '@/components/common/Button';
import { cn } from '@/utils/cn';

/**
 * A dialog over the whole app. It is drawn into document.body, so a side panel that moves or clips its children
 * (transform, overflow) cannot shrink it to the panel's width. Escape and the close button call onClose; a click
 * beside the dialog does nothing, so a long task in it is not lost by a stray click.
 */
export type ModalSize = 'sm' | 'md' | 'lg' | 'xl';

const widths: Record<ModalSize, string> = {
  sm: 'sm:max-w-md',
  md: 'sm:max-w-2xl',
  lg: 'sm:max-w-4xl',
  xl: 'sm:max-w-6xl',
};

interface ModalProps {
  title: React.ReactNode;
  subtitle?: React.ReactNode;
  size?: ModalSize;
  /** Fills most of the screen's height, for work that scrolls (a table, a list of columns). */
  tall?: boolean;
  onClose: () => void;
  /** Buttons along the bottom, right aligned; they stay in view while the body scrolls. */
  footer?: React.ReactNode;
  /** Shown at the left of the footer (a status, a hint). */
  footerStart?: React.ReactNode;
  children: React.ReactNode;
  testId?: string;
}

export const Modal: React.FC<ModalProps> = ({ title, subtitle, size = 'md', tall = false, onClose, footer, footerStart, children, testId }) => {
  const { t } = useTranslation();
  const titleId = useId();

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.stopPropagation();
        onClose();
      }
    };
    window.addEventListener('keydown', onKey);
    const overflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = overflow;
    };
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[1000] flex items-stretch sm:items-center justify-center sm:p-6"
         style={{ backgroundColor: 'var(--gn-modal-overlay, rgba(15, 23, 42, 0.55))' }}>
      <div role="dialog" aria-modal="true" aria-labelledby={titleId} data-testid={testId}
           className={cn('bg-gn-modal text-gn-primary w-full sm:rounded-xl shadow-2xl flex flex-col border border-gn-light',
             widths[size], tall ? 'h-full sm:h-[min(88vh,960px)]' : 'max-h-full sm:max-h-[88vh]')}
           // the dialog's own clicks must not reach the panel it was opened from (React events cross portals)
           onClick={(e) => e.stopPropagation()}>
        <header className="flex items-start justify-between gap-4 px-6 pt-5 pb-4 border-b border-gn-light shrink-0">
          <div className="min-w-0">
            <h2 id={titleId} className="text-lg font-semibold leading-7 truncate">{title}</h2>
            {subtitle && <p className="text-sm text-gn-muted truncate">{subtitle}</p>}
          </div>
          <IconButton icon={X} label={t('uploads.close')} onClick={onClose} className="-mr-2" />
        </header>
        <div className="flex-1 min-h-0 overflow-y-auto px-6 py-5">{children}</div>
        {(footer || footerStart) && (
          <footer className="flex flex-col-reverse sm:flex-row sm:items-center gap-3 px-6 py-4 border-t border-gn-light shrink-0">
            <div className="flex-1 min-w-0 text-sm text-gn-muted">{footerStart}</div>
            <div className="flex flex-col-reverse sm:flex-row gap-2 sm:justify-end">{footer}</div>
          </footer>
        )}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
